'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { useCartItemsSchedule } from './_hooks/use-cart-items-schedule.hook';

import { ArrivalOrderModal } from '@/app/(main)/(pages)/_components/schedules/arrival-order/arrival-order-modal.component';
import { NearScheduleModal } from '@/app/(main)/(pages)/_components/schedules/near-schedule-modal/near-schedule-modal.component';
import { ScheduledModal } from '@/app/(main)/(pages)/_components/schedules/scheduled/scheduled-modal.component';
import { UnavailableScheduleModal } from '@/app/(main)/(pages)/_components/schedules/unavailable-schedule-modal/unavailable-schedule-modal.component';
import { SchedulesSearchSlot } from '@/app/(main)/(pages)/_components/schedules/_types/schedule-slot.type';
import { ScheduleType } from '@/app/(main)/(pages)/_components/schedules/_types/schedule-type.enum';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { ScheduleSlotActionLabels } from '@/components/ui/schedule-slot-button.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import {
  closeScheduleSelection,
  scheduleSelectionStore,
  selectScheduleSlot,
} from '@/store/schedule-selection/schedule-selection.store';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { isArrivalOrderScheduleType } from '@/utils/cart/format-cart-item-schedule.util';
import { getCartItemEntity } from '@/utils/cart/get-cart-item-entity.util';
import {
  getCartItemScheduleType,
  isScheduleType,
} from '@/utils/cart/get-cart-item-schedule-type.util';
import { getCartTotal } from '@/utils/cart/get-cart-total.util';
import { isScheduleNear } from '@/utils/schedule/is-schedule-near.util';
import { isSchedulePast } from '@/utils/schedule/is-schedule-past.util';

export const SCHEDULE_SELECTION_ERROR_MESSAGE = 'Não foi possível carregar os horários. Tente novamente mais tarde.';

const NO_SLOT_FILTERS = {};
const NO_BUSY_SLOT_IDS: ReadonlySet<string> = new Set<string>();
const SLOTS_CLOCK_INTERVAL_MS = 60 * 1000;

const CART_SLOT_ACTION_LABELS: ScheduleSlotActionLabels = {
  select: 'trocar para este horário',
  selected: 'horário atual',
};

export function ScheduleSelectionHost() {
  const isOpen = useStore(scheduleSelectionStore, (state) => state.isOpen);
  const request = useStore(scheduleSelectionStore, (state) => state.request);

  const [nearSlot, setNearSlot] = useState<SchedulesSearchSlot | null>(null);
  const [isUnavailableOpen, setIsUnavailableOpen] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setNow(new Date());

    const clock = setInterval(() => setNow(new Date()), SLOTS_CLOCK_INTERVAL_MS);

    return () => clearInterval(clock);
  }, [isOpen]);

  const pathname = usePathname();
  const prevPathnameRef = useRef(pathname);

  useEffect(() => {
    if (prevPathnameRef.current === pathname) {
      return;
    }

    prevPathnameRef.current = pathname;
    closeScheduleSelection();
  }, [pathname]);

  useEffect(() => {
    if (!isOpen) {
      setNearSlot(null);
      setIsUnavailableOpen(false);
    }
  }, [isOpen]);

  const items = isOpen ? request?.items : undefined;
  const firstItem = items?.[0];

  const entity = useMemo(() => (firstItem ? getCartItemEntity(firstItem) : null), [firstItem]);
  const scheduleType = useMemo(() => {
    const resolved = firstItem && getCartItemScheduleType(firstItem);

    return isScheduleType(resolved) ? resolved : undefined;
  }, [firstItem]);

  const { isLoading, scheduleId, hasError } = useCartItemsSchedule(items, scheduleType);
  const scheduleIds = scheduleId ? [scheduleId] : [];

  if (!firstItem || !entity) {
    return null;
  }

  const isArrivalOrder = isArrivalOrderScheduleType(scheduleType);
  const showsSlots = isOpen && nearSlot === null && !isUnavailableOpen;
  const markedSlotIds = new Set(firstItem.slot.id ? [firstItem.slot.id] : []);

  const errorContent = hasError
    ? (
        <Typography
          variant={TypographyVariant.BODY_TEXT}
          className="text-center text-gray-500 py-6"
          data-testid={DataTestIdEnum.CART_CHANGE_SCHEDULE_MODAL_ERROR}
        >
          {SCHEDULE_SELECTION_ERROR_MESSAGE}
        </Typography>
      )
    : undefined;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      closeScheduleSelection();
    }
  };

  const hasPassed = (slot: SchedulesSearchSlot) => isSchedulePast(isArrivalOrder ? slot.end : slot.start);

  const handleSelectSlot = (slot: SchedulesSearchSlot) => {
    if (slot.id === firstItem.slot.id) {
      closeScheduleSelection();

      return;
    }

    if (hasPassed(slot)) {
      setIsUnavailableOpen(true);

      return;
    }

    if (!isArrivalOrder && isScheduleNear(slot.start)) {
      setNearSlot(slot);

      return;
    }

    selectScheduleSlot(slot);
  };

  const handleUnavailableScheduleClose = () => {
    setIsUnavailableOpen(false);
    setNow(new Date());
  };

  const handleNearScheduleConfirm = () => {
    const confirmedSlot = nearSlot;

    setNearSlot(null);

    if (!confirmedSlot) {
      return;
    }

    if (hasPassed(confirmedSlot)) {
      setIsUnavailableOpen(true);

      return;
    }

    getAnalyticsPlugin().track(AnalyticsEnum.ACCEPTED_NEAR_SCHEDULE_WARNING, {
      partner_id: firstItem.partner.id,
      schedule_type: ScheduleType.SCHEDULED,
    });

    selectScheduleSlot(confirmedSlot);
  };

  return (
    <>
      {isArrivalOrder
        ? (
            <ArrivalOrderModal
              scheduleIds={scheduleIds}
              entityName={entity.name}
              entityAvatar={entity.avatar}
              entityType={entity.type}
              partnerId={firstItem.partner.id}
              price={getCartTotal(items ?? [])}
              open={showsSlots}
              slotFilters={NO_SLOT_FILTERS}
              dataTestId={DataTestIdEnum.CART_CHANGE_SCHEDULE_MODAL}
              closeDataTestId={DataTestIdEnum.CART_CHANGE_SCHEDULE_MODAL_CLOSE}
              isLoadingSchedule={isLoading}
              errorContent={errorContent}
              onOpenChange={handleOpenChange}
              onSelectSlot={handleSelectSlot}
            />
          )
        : (
            <ScheduledModal
              scheduleIds={scheduleIds}
              entityName={entity.name}
              entityAvatar={entity.avatar}
              entityType={entity.type}
              now={now}
              markedSlotIds={markedSlotIds}
              busySlotIds={NO_BUSY_SLOT_IDS}
              open={showsSlots}
              slotFilters={NO_SLOT_FILTERS}
              dataTestId={DataTestIdEnum.CART_CHANGE_SCHEDULE_MODAL}
              closeDataTestId={DataTestIdEnum.CART_CHANGE_SCHEDULE_MODAL_CLOSE}
              toggleExpandDataTestId={DataTestIdEnum.CART_CHANGE_SCHEDULE_MODAL_TOGGLE_EXPAND}
              slotDataTestId={DataTestIdEnum.CART_CHANGE_SCHEDULE_MODAL_SLOT_BUTTON}
              slotActionLabels={CART_SLOT_ACTION_LABELS}
              isLoadingSchedule={isLoading}
              errorContent={errorContent}
              onOpenChange={handleOpenChange}
              onSelectSlot={handleSelectSlot}
            />
          )}

      <NearScheduleModal
        open={nearSlot !== null}
        partnerId={firstItem.partner.id}
        scheduleType={ScheduleType.SCHEDULED}
        onConfirm={handleNearScheduleConfirm}
        onDismiss={() => setNearSlot(null)}
      />

      <UnavailableScheduleModal
        open={isUnavailableOpen}
        onClose={handleUnavailableScheduleClose}
      />
    </>
  );
}
